import { supabase } from './supabaseClient';

// One row per staff member — see core_sip_credentials in
// thekseoul-admin-hub/supabase/migrations/20260913110000_core_sip_calling.sql.
// RLS only ever lets this resolve to the signed-in user's OWN row (or none),
// never anyone else's — sip_password must never be fetched for a roster/
// presence view.
export interface SipCredentialRow {
  id: string;
  user_id: string;
  extension: string;
  sip_password: string;
  sip_server: string;
  ws_url: string;
  display_name: string | null;
  is_active: boolean;
  dnd_enabled: boolean;
}

export async function getMySipCredentials(): Promise<SipCredentialRow | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;
  const { data, error } = await supabase.from('core_sip_credentials').select('*').eq('user_id', user.id).maybeSingle();
  if (error) throw error;
  return (data as SipCredentialRow) ?? null;
}

// Self-service DND toggle — a plain UPDATE on core_sip_credentials is
// admin/admin_low-only (see its RLS policy; the table also holds
// sip_password), so this goes through a security-definer RPC scoped to the
// caller's own row instead (core_set_my_dnd in
// 20260917100000_core_sip_credentials_dnd.sql).
export async function setMyDnd(dnd: boolean): Promise<void> {
  const { error } = await supabase.rpc('core_set_my_dnd', { p_dnd: dnd });
  if (error) throw error;
}

// ───────────────────────── Call Log ─────────────────────────

export type CallDirection = 'outbound' | 'inbound';
export type CallStatus = 'ringing' | 'completed' | 'no_answer' | 'busy' | 'failed' | 'missed' | 'rejected' | 'canceled' | 'transferred';

export interface CallLogRow {
  id: string;
  user_id: string;
  user_name: string | null;
  direction: CallDirection;
  counterparty_number: string | null;
  status: CallStatus;
  duration_seconds: number | null;
  started_at: string;
  ended_at: string | null;
  created_at: string;
  transferred_to: string | null;
  notes: string | null;
  fail_reason: string | null;
}

// Inserted the moment a call starts dialing/ringing — user_id/user_name are
// overwritten server-side by core_call_log_guard() regardless of what's
// sent here, so this is just the initial shape. orderId is optional — only
// calls placed from an order's Call button (Orders list/detail) pass one,
// so this row can later show up in that order's own "Confirmation Calls" list.
export async function startCallLog(direction: CallDirection, counterparty_number: string, orderId?: string): Promise<CallLogRow> {
  const { data, error } = await supabase
    .from('core_call_log')
    .insert({ direction, counterparty_number, status: 'ringing', order_id: orderId ?? null })
    .select('*')
    .single();
  if (error) throw error;
  return data as CallLogRow;
}

// Called once the call ends (answered-then-hung-up, no answer, rejected,
// etc). Only status/duration_seconds/ended_at are actually mutable —
// core_call_log_guard() resets every other column back to its original
// value even if a stray field were sent here.
export async function endCallLog(
  id: string,
  status: CallStatus,
  durationSeconds: number | null,
  transferredTo?: string | null,
  failReason?: string | null,
): Promise<void> {
  const { error } = await supabase
    .from('core_call_log')
    .update({
      status,
      duration_seconds: durationSeconds,
      ended_at: new Date().toISOString(),
      ...(transferredTo !== undefined ? { transferred_to: transferredTo } : {}),
      ...(failReason !== undefined ? { fail_reason: failReason } : {}),
    })
    .eq('id', id);
  if (error) throw error;
}

// Own-history only (RLS already enforces this — the explicit .eq is just
// for clarity/consistency with listSmsLog's own-scoped call, and keeps the
// query intent obvious without reading the migration).
export async function listMyCallLog(userId: string): Promise<CallLogRow[]> {
  const { data, error } = await supabase
    .from('core_call_log')
    .select('*')
    .eq('user_id', userId)
    .order('started_at', { ascending: false })
    .limit(300);
  if (error) throw error;
  return (data as CallLogRow[]) ?? [];
}

export async function updateCallNotes(id: string, notes: string): Promise<void> {
  const { error } = await supabase.from('core_call_log').update({ notes }).eq('id', id);
  if (error) throw error;
}

// Calls made about a specific order (Orders list/detail Call buttons) —
// goes through a security-definer RPC rather than a plain select, since
// core_call_log's own RLS only shows "your own calls OR admin/admin_low"
// and a colleague's confirmation call should still be visible on the order.
export interface OrderCallRow {
  id: string;
  user_name: string | null;
  direction: CallDirection;
  status: CallStatus;
  duration_seconds: number | null;
  started_at: string;
  ended_at: string | null;
  notes: string | null;
}

export async function listOrderCalls(orderId: string): Promise<OrderCallRow[]> {
  const { data, error } = await supabase.rpc('core_list_order_calls', { p_order_id: orderId });
  if (error) throw error;
  return (data as OrderCallRow[]) ?? [];
}

// ───────────────────────── Transfer ─────────────────────────

export interface TransferTarget {
  user_id: string;
  extension: string;
  display_name: string;
}

// Any signed-in staff member can see who else has a live extension to
// transfer a call to — the RPC only ever returns extension/display_name,
// never sip_password (see core_list_transfer_targets in
// 20260914110000_core_call_transfer_and_lookup.sql).
export async function listTransferTargets(): Promise<TransferTarget[]> {
  const { data, error } = await supabase.rpc('core_list_transfer_targets');
  if (error) throw error;
  return (data as TransferTarget[]) ?? [];
}

// ───────────────────────── Caller lookup ─────────────────────────

export interface CallerInfo {
  customer_name: string | null;
  orders_count: number;
}

// Matches a phone number against known customers/orders so staff see a
// name instead of a bare number on an incoming call or before dialing.
export async function lookupCaller(phone: string): Promise<CallerInfo | null> {
  const { data, error } = await supabase.rpc('core_lookup_caller', { p_phone: phone });
  if (error) throw error;
  const row = (data as CallerInfo[] | null)?.[0];
  if (!row || !row.customer_name) return null;
  return row;
}
